import {
  RequestForQuoteArchivedFilter,
  type IdCollectionOperatorInput,
  type RequestForQuoteFiltersInput,
} from '~/graphql/graphql';
import { buildFilterTree, type EditableFilterRow } from '~/types/filters';

export const REQUEST_FOR_QUOTE_FILTER_FIELD = {
  platformProductIds: 'platformProductIds',
  sellingOrganizationIds: 'sellingOrganizationIds',
  createdByUserIds: 'createdByUserIds',
  hasAcceptedQuote: 'hasAcceptedQuote',
  hasArchivedQuote: 'hasArchivedQuote',
  archived: 'archived',
} as const;

export const HAS_QUOTE_VALUE = { yes: 'true', no: 'false' } as const;

export type RequestForQuoteFilterRow = EditableFilterRow;

const COLLECTION_OPERATORS = ['in', 'notIn'] as const;
const EQUALITY_OPERATORS = ['eq'] as const;

const COLLECTION_FIELDS: readonly string[] = [
  REQUEST_FOR_QUOTE_FILTER_FIELD.platformProductIds,
  REQUEST_FOR_QUOTE_FILTER_FIELD.sellingOrganizationIds,
  REQUEST_FOR_QUOTE_FILTER_FIELD.createdByUserIds,
];

export function newRequestForQuoteFilterRowId(): string {
  return crypto.randomUUID();
}

export function isCollectionField(field: string): boolean {
  return COLLECTION_FIELDS.includes(field);
}

export function operatorsForField(field: string): readonly string[] | null {
  if (isCollectionField(field)) {
    return COLLECTION_OPERATORS;
  }
  if (
    field === REQUEST_FOR_QUOTE_FILTER_FIELD.hasAcceptedQuote ||
    field === REQUEST_FOR_QUOTE_FILTER_FIELD.hasArchivedQuote ||
    field === REQUEST_FOR_QUOTE_FILTER_FIELD.archived
  ) {
    return EQUALITY_OPERATORS;
  }
  return null;
}

function hasKnownOperator(row: RequestForQuoteFilterRow): boolean {
  const operators = operatorsForField(row.field);
  return operators !== null && operators.includes(row.operator);
}

function collectionClause(
  field: string,
  operatorInput: IdCollectionOperatorInput
): RequestForQuoteFiltersInput {
  if (field === REQUEST_FOR_QUOTE_FILTER_FIELD.sellingOrganizationIds) {
    return { sellingOrganizationIds: operatorInput };
  }
  if (field === REQUEST_FOR_QUOTE_FILTER_FIELD.createdByUserIds) {
    return { createdByUserIds: operatorInput };
  }
  return { platformProductIds: operatorInput };
}

export function rowToClause(
  row: RequestForQuoteFilterRow
): RequestForQuoteFiltersInput | null {
  if (!hasKnownOperator(row)) return null;

  if (isCollectionField(row.field)) {
    const selectedIds = (Array.isArray(row.value) ? row.value : []).filter(
      Boolean
    );
    if (selectedIds.length === 0) return null;
    return collectionClause(
      row.field,
      row.operator === 'notIn' ? { notIn: selectedIds } : { in: selectedIds }
    );
  }

  if (
    row.field === REQUEST_FOR_QUOTE_FILTER_FIELD.hasAcceptedQuote ||
    row.field === REQUEST_FOR_QUOTE_FILTER_FIELD.hasArchivedQuote
  ) {
    const hasQuote = hasQuoteOf(row.value);
    if (hasQuote === undefined) return null;
    return row.field === REQUEST_FOR_QUOTE_FILTER_FIELD.hasAcceptedQuote
      ? { hasAcceptedQuote: hasQuote }
      : { hasArchivedQuote: hasQuote };
  }

  return null;
}

function hasQuoteOf(value: RequestForQuoteFilterRow['value']) {
  if (value === HAS_QUOTE_VALUE.yes) return true;
  if (value === HAS_QUOTE_VALUE.no) return false;
  return undefined;
}

function archivedFilterOf(
  row: RequestForQuoteFilterRow
): RequestForQuoteArchivedFilter | undefined {
  if (
    row.field !== REQUEST_FOR_QUOTE_FILTER_FIELD.archived ||
    !hasKnownOperator(row)
  ) {
    return undefined;
  }
  return Object.values(RequestForQuoteArchivedFilter).find(
    (archivedFilter) => archivedFilter === row.value
  );
}

export function archivedOf(
  rows: RequestForQuoteFilterRow[]
): RequestForQuoteArchivedFilter | undefined {
  const chosenArchivedFilters = rows.map(archivedFilterOf);
  if (chosenArchivedFilters.includes(RequestForQuoteArchivedFilter.Only)) {
    return RequestForQuoteArchivedFilter.Only;
  }
  if (chosenArchivedFilters.includes(RequestForQuoteArchivedFilter.Include)) {
    return RequestForQuoteArchivedFilter.Include;
  }
  return undefined;
}

export function isArchivedRow(row: RequestForQuoteFilterRow): boolean {
  return row.field === REQUEST_FOR_QUOTE_FILTER_FIELD.archived;
}

export function withoutArchivedRows(
  rows: RequestForQuoteFilterRow[]
): RequestForQuoteFilterRow[] {
  return rows.filter((row) => !isArchivedRow(row));
}

export function buildRequestForQuoteFiltersInput(
  rows: RequestForQuoteFilterRow[]
): RequestForQuoteFiltersInput | undefined {
  return buildFilterTree(withoutArchivedRows(rows), rowToClause);
}

export function activeRowCount(rows: RequestForQuoteFilterRow[]): number {
  return rows.filter(
    (row) => rowToClause(row) !== null || archivedFilterOf(row) !== undefined
  ).length;
}
